/**
 * STU — Studievaardigheden rulebook.
 *
 * Sourced from DEFAULT_POLICY (Studievaardig entries) +
 * MODULE_AUTHORING_GUIDE §9.
 *
 * v2 (mei 2026, sessie 2) — uitgebreid n.a.v.
 * docs/archive/VISUALS_QUESTIONTYPES_RULEBOOK_AUDIT_2026.md (P1-2):
 *   - familyVisualHints uitgebreid van 1 → 18 entries; dekt alle STU-families
 *     (grafiek, tabel, kaart, route, mindmap, flowchart, inhoudsopgave,
 *     diagram, woordenboek/alfabet, plannen, samenvatten).
 *   - customTypeRoles uitgebreid met schedule_reading, ordering, matching,
 *     en map_locate_* varianten.
 *   - preferredAidTypes uitgebreid (mindmap, flowchart, tableOfContents,
 *     splitDiagram, ruler).
 *   - discouragedTypes: true_false_series toegevoegd (recognition vs. echte
 *     informatie-extractie).
 *
 * NB: tables, bar/line/pie charts zijn in dit project gemodelleerd als
 * QUESTION TYPES (table_reading, bar_chart, …), niet als VisualAidType.
 * Per-family rendering komt via de question-renderer in plaats van een
 * stimulusAid; daarom verwijzen veel hints hier naar de QUESTION TYPE in
 * plaats van een visual.
 */

import type { ModuleRulebook } from './types'

export const STU_RULEBOOK: ModuleRulebook = {
  moduleId: 'Studievaardig',
  shortCode: 'STU',
  displayName: 'Studievaardigheden',

  didactics: {
    principles: [
      'informatie_uit_grafiek',
      'tabel_lezen',
      'kaart_interpreteren',
      'plan_en_strategie',
      'schemavisualisatie',
      'tekst_naar_schema',
    ],
    schoolbookReferences: ['Blits', 'Topondernemers'],
    misconceptionDomain: 'stu',
    coreSummary:
      'Studievaardigheden: tabellen, grafieken, kaarten, schemas, mindmaps, en strategieën voor samenvatten en plannen. Kern: informatie uit visueel/schematisch materiaal extraheren of zelf structureren.',
  },

  notation: {},

  content: {
    distractorMisconceptionPattern:
      'strategie|samenvatten|hoofd(gedachte|idee)|legenda|as-waarde|kolom|rij|cel|route|schema',
  },

  explanation: {
    requireRuleReference: true,
    checkStrategyMinTier: 9,
    preferredStrategyKeywords: [
      'tabel lezen',
      'as-waarde',
      'legenda',
      'hoofdgedachte',
      'schemavorm',
      'kolom',
      'rij',
      'route',
      'kompasrichting',
    ],
  },

  visuals: {
    preferredAidTypes: [
      'timeline',
      'readingPassage',
      'textStructureSchema',
      'mindmap',
      'flowchart',
      'tableOfContents',
      'splitDiagram',
      'ruler',
    ],
    familyVisualHints: [
      // — Tijdlijn —
      {
        familyIdPattern: '^STU-TIJDLIJN',
        preferredStimulus: 'timeline',
        note: 'Tijdlijn als primaire stimulus: gebeurtenissen op chronologische as plaatsen of aflezen.',
      },

      // — Grafieken (renderers zijn question types: bar_chart, line_chart, pie_chart) —
      {
        familyIdPattern: '^STU-STAAF',
        autoExemptEmbedded: true,
        note: 'Staafdiagram: gebruik bar_chart question type als stimulus; categorie + as-waarde aflezen.',
      },
      {
        familyIdPattern: '^STU-LIJN',
        autoExemptEmbedded: true,
        note: 'Lijngrafiek: gebruik line_chart question type; trend tussen punten interpreteren.',
      },
      {
        familyIdPattern: '^STU-CIRKEL',
        autoExemptEmbedded: true,
        note: 'Cirkeldiagram: gebruik pie_chart question type; percentage-aandeel per categorie.',
      },
      {
        familyIdPattern: '^STU-GRAFIEK',
        autoExemptEmbedded: true,
        note: 'Algemene grafiek-vraag: kies bar_chart/line_chart/pie_chart op basis van data-aard (categorisch/continu/proportioneel).',
      },

      // — Tabellen —
      {
        familyIdPattern: '^STU-TABEL',
        autoExemptEmbedded: true,
        note: 'Tabel-vraag: gebruik table_reading question type; specifieke cel/rij/kolom benoemen in vraag.',
      },

      // — Kaarten en routes —
      {
        familyIdPattern: '^STU-KAART',
        autoExemptEmbedded: true,
        note: 'Plattegrond/kaart-vraag: gebruik map_locate_point of map_locate_region; legenda en kompasroos verplicht.',
      },
      {
        familyIdPattern: '^STU-ROUTE',
        autoExemptEmbedded: true,
        note: 'Route-vraag: gebruik map_locate_point voor tussenpunten; richting + afstand bij elke stap.',
      },

      // — Schemas: mindmap, flowchart, diagram —
      {
        familyIdPattern: '^STU-MINDMAP',
        preferredStimulus: 'mindmap',
        note: 'Mindmap als stimulus: centrale concept + takken; vraag naar hiërarchie of relatie tussen knopen.',
      },
      {
        familyIdPattern: '^STU-FLOWCHART',
        preferredStimulus: 'flowchart',
        note: 'Stroomdiagram: beslis- of volgorde-vraag; volg pad door diamanten/blokken.',
      },
      {
        familyIdPattern: '^STU-DIAGRAM',
        preferredStimulus: 'splitDiagram',
        note: 'Algemeen schema (b.v. Venn, split-diagram): relatie/overlap tussen categorieën.',
      },

      // — Tekst-structuur: inhoudsopgave, samenvatten, notities —
      {
        familyIdPattern: '^STU-INHOUDSOPGAVE',
        preferredStimulus: 'tableOfContents',
        note: 'Inhoudsopgave als stimulus: hoofdstuk/paragraaf vinden op basis van pagina-nummer of titel.',
      },
      {
        familyIdPattern: '^STU-NOTITIES',
        preferredStimulus: 'readingPassage',
        preferredSupport: 'textStructureSchema',
        note: 'Notities maken: passage als stimulus + schemavorm voor hoofd/sub-idee.',
      },
      {
        familyIdPattern: '^STU-SAMENVATTEN',
        preferredStimulus: 'readingPassage',
        preferredSupport: 'textStructureSchema',
        note: 'Samenvatten: passage als stimulus, structuurschema als steiger voor hoofdgedachte vs detail.',
      },
      {
        familyIdPattern: '^STU-COMBINEER',
        preferredStimulus: 'readingPassage',
        note: 'Tekst + visual combineren: passage met bijgevoegd schema/grafiek; vraag bevraagt beide bronnen.',
      },

      // — Woordenboek-vaardigheden: alfabet, woordenboek, bibliotheek —
      {
        familyIdPattern: '^STU-ALFABET',
        autoExemptEmbedded: true,
        note: 'Alfabetische ordening: gebruik ordering question type; geen visual nodig.',
      },
      {
        familyIdPattern: '^STU-WOORDENBOEK',
        preferredStimulus: 'readingPassage',
        note: 'Woordenboek-fragment als stimulus (passage-formaat); lemma/betekenis/woordsoort herkennen.',
      },
      {
        familyIdPattern: '^STU-BIBLIOTHEEK',
        autoExemptEmbedded: true,
        note: 'Bibliotheek-systeem (SISO/genre): categorize of matching question type; geen visual maar duidelijke categorie-labels.',
      },

      // — Internet-vaardigheden —
      {
        familyIdPattern: '^STU-INTERNET',
        preferredStimulus: 'readingPassage',
        note: 'Internet-bron beoordelen: korte tekst/zoekresultaat als stimulus; betrouwbaarheid + datum bevragen.',
      },

      // — Plannen —
      {
        familyIdPattern: '^STU-PLANNEN',
        autoExemptEmbedded: true,
        note: 'Planning-vraag: gebruik schedule_reading of ordering question type; rooster + prioriteit.',
      },
    ],
  },

  questionTypes: {
    preferredTypes: [
      'multiple_choice',
      'table_reading',
      'bar_chart',
      'line_chart',
      'pie_chart',
      'schedule_reading',
      'ordering',
      'matching',
      'categorize',
      'map_locate_point',
      'map_locate_region',
    ],
    discouragedTypes: [
      {
        type: 'true_false_series',
        reason:
          'Recognition-only — bij STU draait alles om informatie-extractie uit een visual/tabel/grafiek. T/F-stelling toetst herkenning, niet aflezen. Gebruik table_reading / bar_chart / map_locate_point in plaats daarvan. UITZONDERING: per chart-familie (STAAF/LIJN/CIRKEL/GRAFIEK) is één true_false_series-variant toegestaan als diversiteits-slot — meerdere stellingen tegelijk over dezelfde grafiek tonen breed begrip (alle as-waarden, niet één punt). Zie familyTypeHint voor STU-(STAAF|LIJN|CIRKEL|GRAFIEK).',
      },
    ],
    customTypeRoles: [
      {
        type: 'table_reading',
        didacticRole: 'concept',
        preferredDok: 2,
        appropriateTiers: { min: 3, max: 16 },
        reason:
          'Tabel lezen vraagt directe informatie-extractie — DOK 2 toepassing.',
      },
      {
        type: 'bar_chart',
        didacticRole: 'concept',
        preferredDok: 2,
        appropriateTiers: { min: 3, max: 16 },
        reason: 'Staafdiagram aflezen koppelt as-waarde aan categorie — DOK 2.',
      },
      {
        type: 'line_chart',
        didacticRole: 'strategic',
        preferredDok: 3,
        appropriateTiers: { min: 7, max: 16 },
        reason:
          'Lijngrafieken: trend interpreteren tussen punten is meerlaags redeneren — DOK 3.',
      },
      {
        type: 'pie_chart',
        didacticRole: 'concept',
        preferredDok: 2,
        appropriateTiers: { min: 5, max: 16 },
        reason:
          'Cirkeldiagram: percentage-aandeel aflezen, klassieke representatietaak.',
      },
      {
        type: 'schedule_reading',
        didacticRole: 'concept',
        preferredDok: 2,
        appropriateTiers: { min: 5, max: 16 },
        reason:
          'Rooster/dienstregeling aflezen koppelt tijd aan activiteit — directe toepassing van tabel-vaardigheid.',
      },
      {
        type: 'ordering',
        didacticRole: 'concept',
        preferredDok: 2,
        appropriateTiers: { min: 3, max: 16 },
        reason:
          'Alfabetische ordening en chronologische volgorde: kern-studievaardigheid; ordering-type forceert volledige reeks i.p.v. enkele keuze.',
      },
      {
        type: 'matching',
        didacticRole: 'concept',
        preferredDok: 2,
        appropriateTiers: { min: 4, max: 16 },
        reason:
          'Categorie ↔ voorbeeld koppelen (b.v. SISO-code ↔ genre): toetst categoriseren zonder afleiding van MC-distractors.',
      },
      {
        type: 'categorize',
        didacticRole: 'concept',
        preferredDok: 2,
        appropriateTiers: { min: 5, max: 16 },
        reason:
          'Bronnen indelen (b.v. betrouwbaar/onbetrouwbaar internet), tekstsoorten sorteren — actieve classificatie.',
      },
      {
        type: 'map_locate_point',
        didacticRole: 'concept',
        preferredDok: 2,
        appropriateTiers: { min: 5, max: 16 },
        reason:
          'Plattegrond-vragen: punt aanwijzen op kaart vereist legenda-gebruik en kompasoriëntatie.',
      },
    ],
    familyTypeHints: [
      {
        familyIdPattern: '^STU-(STAAF|LIJN|CIRKEL|GRAFIEK)',
        preferredTypes: [
          'bar_chart',
          'line_chart',
          'pie_chart',
          'table_reading',
        ],
        note: 'Diagram-families: visual-data question-type is de kern — aflezen uit grafiek, niet beweringen beoordelen. Max één true_false_series-variant per familie als diversiteits-slot (meerdere stellingen tegelijk over dezelfde grafiek; toetst breed begrip i.p.v. één aflees-punt). Plus-tier (tier 8+): upgrade de vraagstam van eenvoudig aflezen naar vergelijken/verschil-berekenen/totaal-bepalen/aandeel-uitdrukken — de grafiek blijft DOK 2, maar de cognitieve eis stijgt naar application met meerstaps-redenering (cognitiveCeiling bar_chart=DOK2 maakt DOK 3 onmogelijk; verhoog dus de vraagstam, niet de DOK).',
      },
      {
        familyIdPattern: '^STU-(TABEL|PLANNEN)',
        preferredTypes: ['table_reading', 'schedule_reading', 'ordering'],
        discouragedTypes: ['true_false_series'],
        note: 'Tabel/planning-vaardigheden: rooster of tabel direct aflezen.',
      },
      {
        familyIdPattern: '^STU-(KAART|ROUTE)',
        preferredTypes: [
          'map_locate_point',
          'map_locate_region',
          'ordering',
          'multiple_choice',
        ],
        note: 'Kaart-vaardigheden: legenda-gebruik en kompasoriëntatie testen via locatie-vragen.',
      },
      {
        familyIdPattern:
          '^STU-(MINDMAP|FLOWCHART|DIAGRAM|INHOUDSOPGAVE|TIJDLIJN)',
        preferredTypes: ['matching', 'ordering', 'fill_in', 'multiple_choice'],
        note: 'Schemavormen: structuur-onderdelen koppelen of in volgorde zetten — past bij visualisatie-doel.',
      },
      {
        familyIdPattern: '^STU-(ALFABET|WOORDENBOEK|BIBLIOTHEEK)',
        preferredTypes: ['ordering', 'matching', 'multiple_choice'],
        note: 'Naslagvaardigheden: alfabetisch ordenen en bron-categorie matchen.',
      },
      {
        familyIdPattern: '^STU-(SAMENVATTEN|NOTITIES)',
        preferredTypes: ['text_highlight', 'multiple_choice', 'categorize'],
        discouragedTypes: ['true_false_series'],
        note: 'Samenvatten/notities: kern markeren of detail-vs-hoofdgedachte onderscheiden.',
      },
    ],
  },

  distractorTraps: [
    {
      id: 'neighbor-cell',
      label: 'Naburige cel/waarde',
      description:
        'Antwoord uit een aangrenzende cel of nabije waarde in de tabel/grafiek.',
    },
    {
      id: 'wrong-axis',
      label: 'Verkeerde as',
      description: 'Waarde van x-as i.p.v. y-as aflezen (of andersom).',
    },
    {
      id: 'legend-ignored',
      label: 'Legenda genegeerd',
      description:
        'Bij meerlagige grafiek of kaart: lijn/kleur van een andere categorie aflezen.',
    },
    {
      id: 'wrong-direction',
      label: 'Verkeerde richting',
      description:
        'Bij route- of kaart-vraag: noord/zuid of links/rechts verwisseld.',
    },
    {
      id: 'detail-vs-mainidea',
      label: 'Detail i.p.v. hoofdgedachte',
      description:
        'Bij samenvatten/notities: een detail uit de tekst kiezen i.p.v. de hoofdgedachte.',
    },
  ],

  keyRules: [
    'Tabel/grafiek/kaart-stimulus altijd verplicht — geen abstracte vraag zonder bron',
    'Vraag verwijst altijd naar specifieke cel/punt/lijn in de visual',
    'Trap-distractor: naburige cel/waarde of verkeerde as',
    'Legenda-gebruik expliciet benoemen bij meerlagige grafieken en kaarten',
    'Kompasrichting (N/Z/O/W) verplicht bij kaart/route-vragen',
    'true_false_series vermijden — STU draait om aflezen, niet herkennen; uitzondering: max één t/f-variant per chart-familie (STAAF/LIJN/CIRKEL/GRAFIEK) als diversiteits-slot',
    'Plus-tier chart-vragen (tier 8+): upgrade de vraagstam naar vergelijken/verschil/totaal/aandeel i.p.v. simpel aflezen — bar_chart heeft cognitiveCeiling DOK2, dus verhoog de cognitieve eis via de vraagstam, niet via de DOK',
  ],

  // GSR-25 (PR-9): per-module override copy migrated from inline
  // `moduleMessages` maps in didacticDepth.ts.
  qualityMessages: {
    explanationDepthRequirement: 'STU explanation must describe strategy',
    distractorMisconceptionRequirement:
      'STU distractors should reference strategy error',
  },

  authoringGuideAnchor: '#9-stu--studievaardigheden',
}
